/**
 * Stale-tab reload (force-v2, 2026-10-01). An open tab — or a phone's resumed browser — keeps the
 * bundle it was loaded with, including its old portal routing, until something reloads it. Deploys
 * never delete old chunks, so the chunk-error reload in index.html never fires.
 *
 * The tab knows its own entry script; /health reports the entry the server's CURRENT index.html
 * loads (`clientEntry`, shared/clientBuild.ts — re-read when index.html changes on disk, see
 * server/lib/clientBuild.ts). Checked on start, every 5 minutes and whenever the tab becomes
 * visible. On a mismatch the tab is marked stale and hard-reloads:
 *   - at the next in-app navigation (the user is leaving the page anyway — no half-typed form lost), or
 *   - when a tab that was hidden for ≥ 1 hour comes back (the "left it open for days" case), but
 *     ONLY if the page holds no entered text (a draft, a composer, a guest row) — otherwise it waits
 *     for the next navigation.
 *
 * SCOPE (review 2026-10-01): signed-in CLIENT-SIDE roles (client, collaborator, planner) on the
 * portal host (lamirage.co) only. Staff, kiosks (photographer tablet, print station, driver,
 * signage, Urbania TV), live-watch hosts (*.lamirage.live), wedding-website subdomains and custom
 * domains never run it — those surfaces are out of force-v2's scope and a reload there can cost a
 * live upload or a stream. Never INTO a live-stream, payment, editor or kiosk path (waits for the
 * next navigation), never in the store app (its bundle ships in the binary), and at most once per
 * server entry per 30 minutes (loop guard: a CDN still serving the old index.html must not cause a
 * reload loop).
 *
 * Accepted: during a rolling reload a tab can see one worker's entry differ from another's for a
 * few seconds; the worst case is one extra reload at the tab's next navigation, bounded by the guard.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { entryFromHtml } from "@shared/clientBuild";
import { isAppBuild } from "@/lib/net";
import { isClientSideRole } from "@/v2/lib/flag";

export const STALE_RELOAD_GUARD_MS = 30 * 60 * 1000;
export const STALE_CHECK_EVERY_MS = 5 * 60 * 1000;
export const STALE_LONG_HIDDEN_MS = 60 * 60 * 1000;
const GUARD_KEY = "lm-stale-reload";

/**
 * Never reloaded INTO (the tab waits for the next navigation):
 *   - live stream / replay and money screens — a reload costs a viewer their stream or a payment;
 *   - editors and composers that hold typed work;
 *   - kiosk and staff paths — belt and braces: the role scope already keeps them out.
 */
const EXEMPT = [
  // live + money
  "/live-stream", "/watch", "/live", "/invoices", "/pay", "/inv", "/sign", "/payment",
  "/client/addons", "/dance-program-booking", "/client/catering", "/live-photos", "/website-v2/manage",
  // editors / composers
  "/wedding-website-editor", "/support", "/event-form", "/invitations", "/guests",
  // kiosks + staff
  "/photographer", "/photographer-app", "/print-station", "/driver", "/urbaniatv", "/signage",
  "/admin", "/meeting",
];

/** The portal host. Live watch (*.lamirage.live), couple subdomains and custom domains are not it. */
export function isPortalHost(hostname: string): boolean {
  return hostname === "lamirage.co" || hostname === "www.lamirage.co";
}

export function isStaleReloadExempt(pathname: string): boolean {
  return EXEMPT.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export interface ReloadGuard { target: string; at: number }
type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function readReloadGuard(s: StorageLike | null): ReloadGuard | null {
  try {
    const raw = s?.getItem(GUARD_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.target === "string" && typeof v.at === "number" ? { target: v.target, at: v.at } : null;
  } catch {
    return null;
  }
}

export function writeReloadGuard(s: StorageLike | null, g: ReloadGuard): void {
  try { s?.setItem(GUARD_KEY, JSON.stringify(g)); } catch { /* private mode: the reload still happens once */ }
}

export interface StaleReloadInput {
  own: string | null;
  server: string | null;
  path: string;
  guard: ReloadGuard | null;
  now: number;
  inApp: boolean;
  /** The signed-in user's role (undefined = signed out). Only client-side roles are reloaded. */
  role: string | null | undefined;
  hostname: string;
  /** 'navigate' = a route change (always safe); 'resume' = a long-hidden tab came back. */
  trigger: "navigate" | "resume";
  /** 'resume' only: the page holds text the user typed (see pageHasEnteredText). */
  hasEnteredText?: boolean;
}

export function staleReloadDecision(i: StaleReloadInput): "reload" | "wait" | "skip" {
  if (i.inApp || !isClientSideRole(i.role) || !isPortalHost(i.hostname)) return "skip";
  if (!i.own || !i.server || i.own === i.server) return "skip";
  if (i.guard && i.guard.target === i.server && i.now - i.guard.at < STALE_RELOAD_GUARD_MS) return "skip";
  if (isStaleReloadExempt(i.path)) return "wait";
  if (i.trigger === "resume" && i.hasEnteredText) return "wait";
  return "reload";
}

/**
 * True when the page holds text a user typed or a field they are in. Deliberately over-eager: a
 * pre-filled settings field also counts — the cost of a false positive is only waiting for the
 * next navigation, the cost of a false negative is a lost draft.
 */
export function pageHasEnteredText(doc: Pick<Document, "querySelectorAll" | "activeElement">): boolean {
  const active = doc.activeElement as HTMLElement | null;
  if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT" || active.isContentEditable)) return true;
  const SKIP = new Set(["hidden", "checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);
  for (const el of Array.from(doc.querySelectorAll("input, textarea, [contenteditable='true']")) as HTMLElement[]) {
    if (el.tagName === "TEXTAREA" && (el as HTMLTextAreaElement).value.trim()) return true;
    if (el.tagName === "INPUT") {
      const input = el as HTMLInputElement;
      if (!SKIP.has((input.type || "text").toLowerCase()) && input.value.trim()) return true;
    }
    if (el.getAttribute("contenteditable") === "true" && (el.textContent ?? "").trim()) return true;
  }
  return false;
}

/** The entry script THIS document was loaded by (null in dev). */
export function ownClientEntry(doc: Document = document): string | null {
  return entryFromHtml(
    Array.from(doc.querySelectorAll("script[type='module'][src]")).map((s) => s.outerHTML).join(""),
  );
}

async function fetchServerEntry(): Promise<string | null> {
  try {
    const r = await fetch(`/health?t=${Date.now()}`, { cache: "no-store", credentials: "omit" });
    if (!r.ok) return null;
    const body = await r.json();
    return typeof body?.clientEntry === "string" ? body.clientEntry : null;
  } catch {
    return null;
  }
}

function session(): StorageLike | null {
  try { return window.sessionStorage; } catch { return null; }
}

/**
 * Mount once, high in the tree (App.tsx AppContent), with the signed-in user's role. Does nothing
 * (no /health polling either) unless the role is client-side and the host is the portal host.
 */
export function useStaleBuildReload(role: string | null | undefined): void {
  const [location] = useLocation();
  const staleTarget = useRef<string | null>(null);
  const own = useRef<string | null>(null);
  const roleRef = useRef(role);
  roleRef.current = role;
  const active = typeof window !== "undefined" && !isAppBuild() && isClientSideRole(role)
    && isPortalHost(window.location.hostname);

  const tryReload = (path: string, trigger: "navigate" | "resume") => {
    const target = staleTarget.current;
    if (!target) return;
    const decision = staleReloadDecision({
      own: own.current, server: target, path, guard: readReloadGuard(session()), now: Date.now(), inApp: isAppBuild(),
      role: roleRef.current, hostname: window.location.hostname, trigger,
      hasEnteredText: trigger === "resume" ? pageHasEnteredText(document) : false,
    });
    if (decision !== "reload") return;
    writeReloadGuard(session(), { target, at: Date.now() });
    window.location.reload();
  };

  useEffect(() => {
    if (!active) { staleTarget.current = null; return undefined; }
    own.current = ownClientEntry();
    if (!own.current) return undefined; // dev server: nothing to compare
    let hiddenAt: number | null = null;
    let stopped = false;
    const check = async () => {
      const server = await fetchServerEntry();
      if (stopped) return;
      staleTarget.current = server && server !== own.current ? server : null;
    };
    const onVisibility = async () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      const longHidden = hiddenAt !== null && Date.now() - hiddenAt >= STALE_LONG_HIDDEN_MS;
      hiddenAt = null;
      await check();
      if (longHidden) tryReload(window.location.pathname, "resume");
    };
    void check();
    const timer = window.setInterval(() => { void check(); }, STALE_CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // A route change is the safe moment: the page being left is going away anyway.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    tryReload(location, "navigate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
}
